import { useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function ChangePasswordScreen() {
  const { changePassword, error, successMessage, clearMessages, signOut } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearMessages();

    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await changePassword(newPassword);
    } catch {
      // Error handled in context
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-background via-background to-orange-950/20">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/assets/logo.png" alt="Roamler" className="h-20 w-20 rounded-2xl object-contain drop-shadow-2xl" />
          </div>
          <h1 className="text-3xl font-bold bg-linear-to-r from-primary via-orange-400 to-amber-300 bg-clip-text text-transparent mb-2">
            Change Your Password
          </h1>
          <p className="text-muted-foreground">
            You must set a new password before continuing.
          </p>
        </div>

        <div className="backdrop-blur-2xl bg-card/50 border border-primary/30 rounded-2xl p-8 shadow-xl">
          {displayError && (
            <div className="mb-6 p-4 rounded-lg bg-destructive/20 border border-destructive/50 text-destructive text-sm">
              {displayError}
            </div>
          )}
          {successMessage && (
            <div className="mb-6 p-4 rounded-lg bg-green-500/20 border border-green-500/50 text-green-400 text-sm">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={8}
                className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border focus:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary/20 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={8}
                className="w-full px-4 py-3 rounded-lg bg-background/50 border border-border focus:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary/20 transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !newPassword || !confirmPassword}
              className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Changing...' : 'Set New Password'}
            </button>
          </form>

          <button
            onClick={() => signOut()}
            className="w-full mt-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign out instead
          </button>
        </div>
      </div>
    </div>
  );
}
